import React, { useMemo } from "react";
import { Box, styled } from "@mui/material";
import StewartTable, {
  StewartTableColumn,
} from "controls/global/stewart-table/StewartTable";
import { EmptyStateType } from "controls/global/stewart-table/StewartTableBodyEmpty";
import StewartTableFooterPaginator from "controls/global/stewart-table/StewartTableFooterPaginator";
import { Order } from "controls/global/stewart-table/stewartTableHelper";
import { PremiumReceivablesReceiptRecord } from "entities/UIModel/PremiumReceivablesReceiptRecord";
import { formatCurrency } from "./arColumns";
import StatsBar, { StatsBarMetric } from "pages/utils/StatsBar";

const ScrollContainer = styled("div")({
  maxHeight: "calc(100vh - 500px)",
  minHeight: "400px",
  overflow: "auto",
  "& .MuiTableContainer-root": {
    maxHeight: "none",
    minHeight: "auto",
    overflow: "visible",
  },
});

const ScrollInner = styled("div")({
  display: "inline-block",
  minWidth: "100%",
});

export interface ReceiptsTableTotals {
  debits: number;
  credits: number;
  net: number;
  distinctAgents: number;
}

interface ReceiptsTableProps {
  records: PremiumReceivablesReceiptRecord[];
  columns: StewartTableColumn<PremiumReceivablesReceiptRecord>[];
  hiddenColumns?: (keyof PremiumReceivablesReceiptRecord)[];
  // True total across the whole filtered result set (from the backend),
  // not just the current page — used for the "All Entries" stat and the
  // paginator, since `records` only ever holds one page's worth of rows.
  totalRows: number;
  totalPages: number;
  rowsPerPage: number;
  onRowsPerPageChange: (count: number) => void;
  page: number;
  onPageChange: (page: number) => void;
  totals: ReceiptsTableTotals;
  onColumnResize: (property: keyof PremiumReceivablesReceiptRecord, newWidth: number) => void;
  onColumnsModified: (next: StewartTableColumn<PremiumReceivablesReceiptRecord>[]) => void;
  // "-Foo" = desc Foo, "Foo" = asc Foo — same convention as TransactionsTable.
  sortColumn?: string;
  onSortChange?: (next: string) => void;
}

const ReceiptsTable = ({
  records,
  columns,
  hiddenColumns = [],
  totalRows,
  totalPages,
  rowsPerPage,
  onRowsPerPageChange,
  page,
  onPageChange,
  totals,
  onColumnResize,
  onColumnsModified,
  sortColumn,
  onSortChange,
}: ReceiptsTableProps) => {
  const orderBy = useMemo<
    keyof PremiumReceivablesReceiptRecord | undefined
  >(() => {
    if (!sortColumn) return undefined;
    const col = sortColumn.startsWith("-") ? sortColumn.slice(1) : sortColumn;
    return col as keyof PremiumReceivablesReceiptRecord;
  }, [sortColumn]);
  const order: Order = sortColumn?.startsWith("-") ? "desc" : "asc";

  const handleRequestSort = (property: keyof PremiumReceivablesReceiptRecord) => {
    const propStr = String(property);
    const currentColumn = sortColumn?.startsWith("-")
      ? sortColumn.slice(1)
      : sortColumn;
    const currentDesc = sortColumn?.startsWith("-") ?? false;
    const nextDesc = currentColumn === propStr ? !currentDesc : false;
    onSortChange?.(nextDesc ? `-${propStr}` : propStr);
  };

    const statsMetrics = useMemo<StatsBarMetric[]>(
    () => [
      { label: "Total Debits", value: formatCurrency(totals.debits) },
      { label: "Total Credits", value: formatCurrency(totals.credits) },
      { label: "Net", value: formatCurrency(totals.net) },
      {
        label: "Distinct Agents",
        value: totals.distinctAgents.toLocaleString(),
      },
    ],
    [totals]
  );

  const emptyStateType =
    records.length === 0 ? EmptyStateType.NO_RESULTS : EmptyStateType.DEFAULT;

  const noRowsMessage = ["No receipts found. Adjust your filters and try again."];

  return (
    <Box>
      <ScrollContainer>
        <ScrollInner>
          <StewartTable
            cols={columns}
            rows={records}
            page={page}
            rowsPerPage={rowsPerPage}
            showActionRow={true}
            noRowsMessage={noRowsMessage}
            orderBy={orderBy}
            order={order}
            useDbPagingSorting={true}
            onRequestSort={handleRequestSort}
            onColumnResize={onColumnResize}
            onColumnsModified={onColumnsModified}
            hiddenColumns={hiddenColumns}
            emptyStateType={emptyStateType}
          />
        </ScrollInner>
      </ScrollContainer>
      <StatsBar
        highlight={{
          label: "All Entries",
          value: totalRows.toLocaleString(),
        }}
        metrics={statsMetrics}
      />
      <StewartTableFooterPaginator
        totalRows={totalRows}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        disablePagination={totalRows === 0}
      />
    </Box>
  );
};

export default ReceiptsTable;
